// eslint-disable-next-line import/no-extraneous-dependencies
import React, {
    HTMLAttributes,
    ReactElement, ReactNode,
} from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
    DndContext,
    DragCancelEvent,
    DragEndEvent,
    DragMoveEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor, closestCenter,
    useSensor, useSensors,
} from "@dnd-kit/core";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import Draggable from "./Draggable";
import Droppable from "./Droppable";

type DraggableOverrides = "onDragEnd" | "onDragStart" | "onDragOver" | "onDragMove" | "onDragCancel";
export interface DragDropContainerProps extends Omit<HTMLAttributes<HTMLDivElement>, DraggableOverrides> {
    children?: ReactNode;
    onDragEnd: (event: DragEndEvent) => void;
    onDragStart?: (event: DragStartEvent) => void;
    onDragOver?: (event: DragOverEvent) => void;
    onDragMove?: (event: DragMoveEvent) => void;
    onDragCancel?: (event: DragCancelEvent) => void;
    shouldCenterOnDrag?: boolean;
}

const DragDropContainerElement = React.forwardRef((
    args: DragDropContainerProps,
    ref: React.ForwardedRef<HTMLDivElement>,
): ReactElement => {
    const {
        children,
        onDragEnd,
        onDragStart,
        shouldCenterOnDrag,
        onDragOver,
        onDragMove,
        onDragCancel,
        ...rest
    } = args;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
    );

    return (
        <DndContext
            modifiers={shouldCenterOnDrag ? [snapCenterToCursor] : undefined}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onDragMove={onDragMove}
            onDragOver={onDragOver}
            onDragCancel={onDragCancel}
        >
            <div
                ref={ref}
                {...rest}
            >
                {children}
            </div>
        </DndContext>
    );
});

DragDropContainerElement.defaultProps = {
    onDragStart: undefined,
    shouldCenterOnDrag: false,
    onDragOver: undefined,
};

type DropdownType = typeof DragDropContainerElement & {
    Droppable: typeof Droppable,
    Draggable: typeof Draggable
    DragOverlay: typeof DragOverlay
}

const DragDropContainer = DragDropContainerElement as DropdownType;
DragDropContainer.Droppable = Droppable;
DragDropContainer.Draggable = Draggable;
DragDropContainer.DragOverlay = DragOverlay;

export default DragDropContainer;
